import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { CheckinClientService } from '@services/api/checkin/checkin-client.service';
import { ICheckinModel } from '@services/api/checkin/checkin-model.interface';
import { PaginatedResponse } from '@services/api/generic-api-client.service';
import { IApiQueryOptions, IOrderOptions } from '@services/api/models/api-query-params.model';
import { LogService } from '@services/util/log.service';
import { parseISO } from 'date-fns';
import { catchError, map, Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { CheckinFilter } from '../models/checkin-filter.model';
import { CheckinRow, PaginatedCheckins } from '../models/checkin.model';

@Injectable({
  providedIn: 'root'
})
export class CheckinService {
  constructor(
    private readonly checkinClient: CheckinClientService,
    private readonly log: LogService
  ) {}

  /**
   * Get paginated checkins with optional sorting and filtering
   *
   * @param page Current page number (1-based)
   * @param itemsPerPage Number of items per page
   * @param sortField Field to sort by
   * @param sortOrder Sort direction
   * @param filter Optional filters
   * @returns Observable with paginated checkins
   */
  getCheckins(
    page: number = 1,
    itemsPerPage: number = 30,
    sortField: string | null = null,
    sortOrder: 'asc' | 'desc' | null = null,
    filter?: CheckinFilter
  ): Observable<PaginatedCheckins> {
    this.log.debug('Fetching checkins with params:', { page, itemsPerPage, sortField, sortOrder, filter });

    // Map frontend field names to API field names for sorting
    const fieldMappings: Record<string, string> = {
      name: 'name',
      email: 'email_address',
      countryCode: 'country_code',
      city: 'city',
      newsletterSignup: 'newsletter_signup',
      createdAt: 'created_at'
    };

    // Define the order object based on sorting parameters
    let orderOptions: IOrderOptions = { created_at: 'desc' }; // Default sorting

    if (sortField && sortOrder) {
      // Use the mapped API field name if available, otherwise use the original
      const apiField = fieldMappings[sortField] || sortField;
      orderOptions = { [apiField]: sortOrder };
      this.log.debug(`Mapping sort field ${sortField} to API field ${apiField}`);
    }

    // Prepare filter objects
    const filterObj: Record<string, any> = {};
    const dateFilterObj: Record<string, any> = {};

    // Apply advanced filter options if provided
    if (filter) {
      if (filter.name) {
        filterObj['name'] = filter.name;
      }

      if (filter.email) {
        filterObj['email_address'] = filter.email;
      }

      if (filter.countryCode) {
        filterObj['country_code'] = filter.countryCode;
      }

      if (filter.city) {
        filterObj['city'] = filter.city;
      }

      if (filter.newsletterSignup !== undefined) {
        filterObj['newsletter_signup'] = filter.newsletterSignup;
      }

      // Handle date filter using the date filtering capabilities
      if (filter.createdAt) {
        dateFilterObj['created_at'] = filter.createdAt;
      }

      this.log.debug('Prepared filter options:', { filterObj, dateFilterObj });
    }

    // Build query options for the API using the date filtering capabilities
    const options: IApiQueryOptions = {
      pagination: {
        page,
        itemsPerPage
      },
      order: orderOptions
    };

    // Only add filters and dateFilters if they have values
    if (Object.keys(filterObj).length > 0) {
      options.filters = filterObj;
    }

    if (Object.keys(dateFilterObj).length > 0) {
      options.dateFilters = dateFilterObj;
    }

    this.log.debug('Applied query options:', options);

    // Use the standard getItems method which handles date filtering
    const request$ = this.checkinClient.getItems('', options);

    return request$.pipe(
      map((response: PaginatedResponse<ICheckinModel>) => {
        // Transform API models to UI models
        const transformedCheckins = response.items.map((checkin) => this.mapToCheckinRow(checkin));

        return {
          items: transformedCheckins,
          totalCount: response.totalItems || 0,
          page,
          itemsPerPage
        };
      })
    );
  }

  /**
   * Delete a checkin
   *
   * @param id ID of the checkin to delete
   * @returns Observable with boolean indicating success
   */
  deleteCheckin(id: string | number): Observable<boolean> {
    this.log.debug('Deleting checkin with ID:', id);

    // Convert string ID to number if needed, as deleteItem expects a number
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;

    return this.checkinClient.deleteItem(numericId).pipe(
      map(() => true),
      catchError((error) => {
        this.log.error('Error deleting checkin:', error);
        return of(false);
      })
    );
  }

  /**
   * Map API checkin model to UI checkin row model
   * @param checkin API checkin model
   * @returns UI checkin row model
   */
  private mapToCheckinRow(checkin: ICheckinModel): CheckinRow {
    return {
      id: checkin.id!,
      name: checkin.name || '',
      email: checkin.email_address || '',
      countryCode: checkin.country_code || '',
      city: checkin.city || '',
      newsletterSignup: checkin.newsletter_signup || false,
      bookings: checkin.bookings
        ? checkin.bookings.map((booking) => ({
            '@id': booking['@id'],
            id: booking.id,
            reference: booking.reservation_code || booking.foreign_reservation_code || `ID: ${booking.id}`,
            name: booking.name || '',
            startDate: booking.start_date || undefined,
            endDate: booking.end_date || undefined,
            flats: activityFlatsOfModels(booking.flats)
          }))
        : [],
      createdAt: checkin.created_at ? parseISO(checkin.created_at) : undefined
    };
  }

  /**
   * Update newsletter subscription status for a checkin
   *
   * @param id Checkin ID
   * @param subscribed Whether the user should be subscribed to the newsletter
   * @returns Observable with boolean indicating success
   */
  updateCheckinNewsletter(id: string | number, subscribed: boolean): Observable<boolean> {
    this.log.debug('Updating newsletter status for checkin:', { id, subscribed });

    // Convert string ID to number if needed
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;

    // Create a partial checkin object with just the newsletter field updated
    const partialCheckin: any = {
      id: numericId,
      newsletter_signup: subscribed
    };

    // Use the standard updateItem method which is available in the GenericApiClientService
    return this.checkinClient.updateItem(partialCheckin).pipe(
      map(() => true),
      catchError((error) => {
        this.log.error('Error updating newsletter status:', error);
        return of(false);
      })
    );
  }
}
